import { db } from '@core/db';
import { Section, SectionPlanningState, SectionStatus } from '@/types';

function isLiveSectionRow(row: Pick<Section, 'deletedAt'> | undefined | null): row is Pick<Section, 'deletedAt'> {
  return Boolean(row && !row.deletedAt);
}

const normalizeSection = (row: any): Section => {
  const nowIso = new Date().toISOString();
  const title = row?.title ?? row?.name ?? 'Sección';
  return {
    id: row.id,
    projectId: row.projectId,
    versionId: row.versionId,
    title,
    name: row?.name ?? title,
    order: row?.order ?? 0,
    level: row?.level ?? 0,
    parentSectionId: row?.parentSectionId ?? undefined,
    status: (row?.status as SectionStatus) ?? 'todo',
    planningState: (row?.planningState as SectionPlanningState) ?? 'normal',
    labels: Array.isArray(row?.labels) ? row.labels : [],
    hidden: row?.hidden ?? false,
    dueDate: row?.dueDate,
    updatedAt: row?.updatedAt ?? nowIso,
    clientId: row?.clientId,
    focusPinned: row?.focusPinned ?? false,
    dirty: row?.dirty ?? false,
    baseRemoteUpdatedAt: row?.baseRemoteUpdatedAt,
    deletedAt: row?.deletedAt,
  };
};

export interface CreateSectionInput {
  id: string;
  projectId: string;
  versionId: string;
  title: string;
  order: number;
  level: number;
  parentSectionId?: string | null;
  status?: SectionStatus;
  planningState?: SectionPlanningState;
  labels?: string[];
  hidden?: boolean;
  dueDate?: string;
  clientId?: string;
  updatedAt?: string;
}

export interface UpdateSectionInput {
  id: string;
  changes: Partial<Section>;
}

export const sectionsRepo = {
  async listByProjectVersion(projectId: string, versionId: string): Promise<Section[]> {
    const rows = await db.sections.where({ projectId, versionId }).sortBy('order');
    return rows.filter((row) => isLiveSectionRow(row as Section)).map(normalizeSection);
  },

  async get(id: string) {
    const row = await db.sections.get(id);
    return isLiveSectionRow(row as Section | undefined) ? normalizeSection(row) : undefined;
  },

  async create(input: CreateSectionInput) {
    const nowIso = input.updatedAt ?? new Date().toISOString();
    const section: Section = {
      id: input.id,
      projectId: input.projectId,
      versionId: input.versionId,
      title: input.title,
      name: input.title,
      order: input.order,
      level: input.level,
      parentSectionId: input.parentSectionId ?? undefined,
      status: input.status ?? 'todo',
      planningState: input.planningState ?? 'normal',
      labels: input.labels ?? [],
      hidden: input.hidden ?? false,
      dueDate: input.dueDate,
      updatedAt: nowIso,
      clientId: input.clientId,
      focusPinned: false,
      dirty: true,
      baseRemoteUpdatedAt: undefined,
      deletedAt: undefined,
    };
    await db.sections.put(section);
    return section;
  },

  async update(id: string, changes: Partial<Section>) {
    const payload = {
      ...changes,
      updatedAt: changes.updatedAt ?? new Date().toISOString(),
      dirty: true,
    } as Partial<Section>;
    if (typeof changes.title === 'string' && !changes.name) {
      (payload as any).name = changes.title;
    }
    await db.sections.update(id, payload);
    return this.get(id);
  },

  async bulkUpdate(updates: UpdateSectionInput[]) {
    const nowIso = new Date().toISOString();
    await db.transaction('rw', db.sections, async () => {
      for (const { id, changes } of updates) {
        const payload = {
          ...changes,
          updatedAt: changes.updatedAt ?? nowIso,
          dirty: true,
        } as Partial<Section>;
        if (typeof changes.title === 'string' && !changes.name) {
          (payload as any).name = changes.title;
        }
        await db.sections.update(id, payload);
      }
    });
  },

  async delete(id: string) {
    const nowIso = new Date().toISOString();
    await db.sections.update(id, {
      deletedAt: nowIso,
      dirty: true,
      updatedAt: nowIso,
    });
  },

  async reorder(sectionId: string, newOrder: number) {
    await db.sections.update(sectionId, { order: newOrder, updatedAt: new Date().toISOString(), dirty: true });
    return db.sections.get(sectionId);
  },
};
